import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { LatencySummary } from "../../types/metrics";
import { callMetricsService } from "../../services/callMetricsService";
import { Activity, Zap, Mic, Volume2, Clock } from "lucide-react";

interface LatencyOverviewProps {
  summary: LatencySummary;
}

export const LatencyOverview: React.FC<LatencyOverviewProps> = ({ summary }) => {
  /**
   * Get color based on latency value (in seconds)
   */
  const getLatencyColor = (value: number, thresholds: { good: number; fair: number }) => {
    if (value <= thresholds.good) return "text-good";
    if (value <= thresholds.fair) return "text-mid";
    return "text-danger";
  };

  /**
   * Get badge variant based on value
   */
  const getBadgeVariant = (value: number, thresholds: { good: number; fair: number }) => {
    if (value <= thresholds.good) return "default"; // green
    if (value <= thresholds.fair) return "secondary"; // yellow
    return "destructive"; // red
  };

  const stats = [
    {
      title: "Overall Response Time",
      description: "End-to-end response latency (STT → LLM → TTS)",
      icon: Clock,
      value: summary.overall_response_time.avg,
      p95: summary.overall_response_time.p95,
      p99: summary.overall_response_time.p99,
      count: summary.overall_response_time.count,
      thresholds: { good: 1.5, fair: 3.0 },
      suffix: "s avg",
    },
    {
      title: "STT Processing",
      description: "Speech-to-text recognition time",
      icon: Mic,
      value: summary.stt.avg,
      p95: summary.stt.p95,
      p99: summary.stt.p99,
      count: summary.stt.count,
      thresholds: { good: 0.5, fair: 1.0 },
      suffix: "s avg",
    },
    {
      title: "LLM Response (TTFT)",
      description: "Time to first token from language model",
      icon: Zap,
      value: summary.llm.ttft.avg,
      p95: summary.llm.ttft.p95,
      p99: summary.llm.ttft.p99,
      count: summary.llm.ttft.count,
      thresholds: { good: 0.8, fair: 1.5 },
      suffix: "s avg",
      extraInfo: {
        label: "Cache Hit Rate",
        value: `${summary.llm.cache_hit_ratio.toFixed(1)}%`,
      },
    },
    {
      title: "TTS Generation (TTFB)",
      description: "Time to first byte of synthesized speech",
      icon: Volume2,
      value: summary.tts.ttfb.avg,
      p95: summary.tts.ttfb.p95,
      p99: summary.tts.ttfb.p99,
      count: summary.tts.ttfb.count,
      thresholds: { good: 0.15, fair: 0.3 },
      suffix: "s avg",
    },
  ];

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">Performance Overview</h2>
        <p className="text-sm text-muted-foreground">
          Key latency metrics for this call
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const latencyColor = getLatencyColor(stat.value, stat.thresholds);
          const badgeVariant = getBadgeVariant(stat.value, stat.thresholds);

          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${latencyColor}`}>
                  {callMetricsService.formatDuration(stat.value)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.description}
                </p>

                <div className="mt-3 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">P95:</span>
                    <Badge variant="outline" className="font-mono">
                      {callMetricsService.formatDuration(stat.p95)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">P99:</span>
                    <Badge variant="outline" className="font-mono">
                      {callMetricsService.formatDuration(stat.p99)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Count:</span>
                    <span className="font-medium">{stat.count}</span>
                  </div>

                  {stat.extraInfo && (
                    <div className="flex items-center justify-between text-xs pt-2 border-t">
                      <span className="text-muted-foreground">
                        {stat.extraInfo.label}:
                      </span>
                      <Badge variant={badgeVariant} className="font-mono">
                        {stat.extraInfo.value}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Total Metrics Count */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center">
            <Activity className="h-4 w-4 mr-2" />
            Metrics Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Total Metrics</div>
              <div className="text-2xl font-bold">{summary.total_metrics}</div>
            </div>
            <div>
              <div className="text-muted-foreground">STT Events</div>
              <div className="text-2xl font-bold">{summary.metric_counts.stt}</div>
            </div>
            <div>
              <div className="text-muted-foreground">LLM Responses</div>
              <div className="text-2xl font-bold">{summary.metric_counts.llm}</div>
            </div>
            <div>
              <div className="text-muted-foreground">TTS Generations</div>
              <div className="text-2xl font-bold">{summary.metric_counts.tts}</div>
            </div>
            <div>
              <div className="text-muted-foreground">VAD Detections</div>
              <div className="text-2xl font-bold">{summary.metric_counts.vad}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
